import React, { memo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCheckDouble,
  FaInstagram,
  FaRobot,
  FaCheckCircle,
  FaUserPlus,
  FaStar,
  FaCommentDots,
} from "react-icons/fa";
import { CHAT_SCRIPT, CHAT_CONTACT, CHAT_PRODUCT } from "./data";

// Simulates the real comment -> follower-gate -> DM-link automation.
// Steps are revealed one by one with a typing indicator before bot messages.
// Self-contained loop, mock data only — no network.
const STEP_DELAY = 1300;
const TYPING_DELAY = 1100;

const AnimatedChat = ({ active }) => {
  const [visible, setVisible] = useState([]);
  const [thinking, setThinking] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    if (!active) {
      clearAll();
      setVisible([]);
      setThinking(false);
      return undefined;
    }

    let step = 0;
    const run = () => {
      const item = CHAT_SCRIPT[step];
      if (!item) {
        timers.current.push(
          setTimeout(() => {
            setVisible([]);
            step = 0;
            run();
          }, 3200)
        );
        return;
      }

      if (item.type === "bot") {
        setThinking(true);
        timers.current.push(
          setTimeout(() => {
            setThinking(false);
            setVisible((v) => [...v, item]);
            step += 1;
            timers.current.push(setTimeout(run, STEP_DELAY));
          }, TYPING_DELAY)
        );
      } else {
        setVisible((v) => [...v, item]);
        step += 1;
        timers.current.push(setTimeout(run, item.type === "check" ? 1600 : STEP_DELAY));
      }
    };

    timers.current.push(setTimeout(run, 600));
    return clearAll;
  }, [active]);

  return (
    <div className="relative h-full w-full">
      {/* Floating notification cards */}
      <motion.div
        className="absolute -left-3 top-6 z-20 hidden rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-xs text-white shadow-lg backdrop-blur-md sm:block"
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="text-green-400">●</span> Keyword matched
      </motion.div>
      <motion.div
        className="absolute -right-2 bottom-10 z-20 hidden rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-xs text-white shadow-lg backdrop-blur-md sm:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
      >
        <span className="text-orange-400">⚡</span> Auto-DM sent
      </motion.div>

      {/* Phone mock */}
      <div className="mx-auto flex h-full w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b0f]/90 shadow-2xl backdrop-blur">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-white/10 bg-white/5 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-pink-500 text-white">
            <FaInstagram />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-white">{CHAT_CONTACT.name}</p>
            <p className="flex items-center gap-1 text-[11px] text-green-400">
              <FaRobot className="text-[10px]" /> Automated by Infuber
            </p>
          </div>
        </div>

        {/* Messages — scroll so longer threads stay visible */}
        <div className="flex min-h-0 flex-1 flex-col justify-end gap-2 overflow-y-auto overscroll-contain p-3 sm:p-3.5 [scrollbar-width:none] [-ms-overflow-style:none]">
          <AnimatePresence initial={false}>
            {visible.map((m) => (
              <Bubble key={m.id} item={m} />
            ))}
          </AnimatePresence>

          <AnimatePresence>
            {thinking && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex justify-start"
              >
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-white/10 px-3 py-2.5">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      className="h-1.5 w-1.5 rounded-full bg-white/70"
                      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                      transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
                    />
                  ))}
                  <span className="ml-1 text-[10px] text-white/50">typing…</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Input bar */}
        <div className="border-t border-white/10 px-4 py-3">
          <div className="flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/40">
            Message…
          </div>
        </div>
      </div>
    </div>
  );
};

const bubbleMotion = {
  initial: { opacity: 0, y: 14, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0 },
  transition: { type: "spring", stiffness: 320, damping: 26 },
};

const Bubble = ({ item }) => {
  // Triggering comment chip
  if (item.type === "trigger") {
    return (
      <motion.div layout {...bubbleMotion} className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-white/60">
          <FaCommentDots className="text-orange-400" /> {CHAT_CONTACT.handle} {item.text}
        </span>
      </motion.div>
    );
  }

  // Follower check status
  if (item.type === "check") {
    return (
      <motion.div layout {...bubbleMotion} className="flex justify-center">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] ${
            item.ok
              ? "border border-green-500/30 bg-green-500/10 text-green-400"
              : "border border-amber-500/30 bg-amber-500/10 text-amber-300"
          }`}
        >
          {item.ok ? <FaCheckCircle /> : <FaUserPlus />} {item.result}
        </span>
      </motion.div>
    );
  }

  // Product card
  if (item.type === "product") {
    return (
      <motion.div layout {...bubbleMotion} className="flex justify-start">
        <div className="max-w-[82%] overflow-hidden rounded-2xl rounded-bl-sm border border-white/10 bg-white/5">
          <div className="flex h-24 items-center justify-center bg-gradient-to-br from-white/10 to-white/[0.02]">
            <span className="text-3xl">🧳</span>
          </div>
          <div className="p-2.5">
            <p className="line-clamp-2 text-[11px] font-medium leading-snug text-white">
              {CHAT_PRODUCT.title}
            </p>
            <p className="mt-1 flex items-center gap-1 text-[10px] text-white/50">
              <span className="font-semibold text-amber-400">{CHAT_PRODUCT.rating}</span>
              <FaStar className="text-amber-400" />
              <span>({CHAT_PRODUCT.reviews})</span>
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // User reply (right, gradient)
  if (item.type === "user") {
    return (
      <motion.div layout {...bubbleMotion} className="flex justify-end">
        <div className="max-w-[78%] rounded-2xl rounded-br-sm bg-gradient-to-br from-violet-500 to-fuchsia-500 px-3.5 py-2 text-[13px] leading-snug text-white">
          {item.text}
        </div>
      </motion.div>
    );
  }

  // Bot message (left, gray) with optional button(s) / link
  return (
    <motion.div layout {...bubbleMotion} className="flex justify-start">
      <div className="max-w-[82%] rounded-2xl rounded-bl-sm bg-white/10 px-3.5 py-2.5 text-[13px] leading-snug text-white/90">
        <p>{item.text}</p>

        {item.link && (
          <a
            href={`https://${CHAT_PRODUCT.link}`}
            onClick={(e) => e.preventDefault()}
            className="mt-1.5 block break-all text-[12px] font-medium text-sky-400 underline"
          >
            {CHAT_PRODUCT.link}
          </a>
        )}

        {item.button && (
          <div className="mt-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-center text-[12px] font-semibold text-white">
            {item.button}
          </div>
        )}

        {item.buttons && (
          <div className="mt-2 space-y-1.5">
            {item.buttons.map((b) => (
              <div
                key={b}
                className="rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-center text-[12px] font-semibold text-white"
              >
                {b}
              </div>
            ))}
          </div>
        )}

        {!item.link && !item.button && !item.buttons && (
          <span className="mt-1 flex items-center justify-end gap-1 text-[10px] text-white/50">
            <FaCheckDouble /> Delivered
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default memo(AnimatedChat);
